/* eslint-disable */
import {
  MeshStandardMaterial,
  MeshPhysicalMaterial,
  Mesh,
  Vector3,
} from 'three';

import * as Helpers from '../helpers';

import { Object3D } from '../Object3D';

import { getRGBFromRAL, metalMat, mirrorGlassMat } from '../Materials';

export class LaraDoor extends Object3D {
  constructor(...args) {
    super(...args);

    const attributes = args[0];

    // load the key resources
    this.resourceManager.loadResources(['wheelSystem'], () => {
      this.update(attributes);
      this.renderFunc();
    });

    this.materials = {
      metal: new MeshStandardMaterial(),
      mirror: new MeshPhysicalMaterial(),
      wood: new MeshStandardMaterial(),
    };

    // Define the materials
    this.materialDefinitions = {
      metal: { ...metalMat },
      mirror: { ...mirrorGlassMat },
      wood: {
        color: 0xffffff,
        roughness: 0.9,
      },
    };

    this.parameters.frameWidth = 28;
    this.parameters.frameThickness = 2;
    this.parameters.mirrorThickness = 4;
    this.parameters.plateThickness = 18;
    this.parameters.margin = 5;
    this.parameters.hangerMargin = 90;

    this.initMaterials();
    this.update(attributes);
  }

  calculateModelParameters() {
    this.parameters.delta =
      this.parameters.frameWidth -
      (2 * this.parameters.frameThickness +
        this.parameters.mirrorThickness +
        this.parameters.plateThickness);
  }

  updateModelParameters(attributes) {
    this.parameters.hangerMargin = 90;

    const doorColor = attributes['system-color']
      ? attributes['system-color']
      : 'system-hamak-black';
    const doorColorParams = doorColor.split('-');
    this.parameters.system = doorColorParams[doorColorParams.length - 2];
    this.parameters.color = doorColorParams[doorColorParams.length - 1];

    this.materialDefinitions.metal.color = getRGBFromRAL(this.parameters.color);
    this.updateMaterial(this.materials.metal, this.materialDefinitions.metal);
  }

  buildModel() {
    const Frame = this.buildFrameGeometry();
    const Body = this.buildBodyGeometry();
    const Mirror = this.buildMirrorGeometry();
    const Hanger = this.buildHangerGeometry();

    Frame.forEach((geometry) => {
      this.group.add(new Mesh(geometry, this.materials.metal));
    });
    if (Body) {
      this.group.add(new Mesh(Body, this.materials.wood));
    }
    if (Mirror) {
      this.group.add(new Mesh(Mirror, this.materials.mirror));
    }
    Hanger.forEach((geometry) => {
      this.group.add(new Mesh(geometry, this.materials.metal));
    });

    this.group.children.forEach((z) => {
      z.castShadow = true;
      z.receiveShadow = true;
    });

    this.group.position.y = -this.parameters.frameWidth - 28;
  }

  buildFrameGeometry() {
    const geometries = [];

    /* create the base geometries */
    const leftStile = Helpers.CreateUFrame(
      this.parameters.height,
      this.parameters.frameWidth,
      false,
      this.parameters.hangerMargin
    );
    const rightStile = Helpers.MirrorObjectX(leftStile, true);
    const topRail = Helpers.CreateUFrame(
      this.parameters.width,
      this.parameters.frameWidth,
      this.parameters.system === 'hamak' ? true : false,
      this.parameters.hangerMargin
    );
    const bottomRail = Helpers.CreateUFrame(
      this.parameters.width,
      this.parameters.frameWidth,
      false,
      this.parameters.hangerMargin
    );
    /* position the geometries */
    leftStile.translate(0, this.parameters.frameWidth, 0);
    rightStile.translate(this.parameters.width, this.parameters.frameWidth, 0);
    Helpers.RotateObject(topRail, null, 90, 'Y');
    topRail.translate(0, this.parameters.frameWidth, this.parameters.height);
    Helpers.RotateObject(bottomRail, null, -90, 'Y');
    bottomRail.translate(this.parameters.width, this.parameters.frameWidth, 0);

    /* push the geometries into the array */
    geometries.push(leftStile);
    geometries.push(rightStile);
    geometries.push(topRail);
    geometries.push(bottomRail);

    return geometries;
  }

  buildBodyGeometry() {
    // const geometries = [];
    /* Create the base objects */
    const plate = Helpers.Box2Points(
      new Vector3(
        this.parameters.margin,
        2 * this.parameters.mirrorThickness,
        this.parameters.margin
      ),
      new Vector3(
        this.parameters.width - this.parameters.margin,
        this.parameters.plateThickness + 2 * this.parameters.mirrorThickness,
        this.parameters.height - this.parameters.margin
      )
    );

    // geometries.push(plate);

    return plate;
  }

  buildMirrorGeometry() {
    // const geometries = [];
    /* Create the base objects */
    const plate = Helpers.Box2Points(
      new Vector3(
        this.parameters.margin,
        this.parameters.delta / 2 +
          this.parameters.frameThickness +
          2 * this.parameters.mirrorThickness,
        this.parameters.margin
      ),
      new Vector3(
        this.parameters.width - this.parameters.margin,
        this.parameters.delta / 2 +
          this.parameters.frameThickness +
          1 * this.parameters.mirrorThickness,
        this.parameters.height - this.parameters.margin
      )
    );

    // geometries.push(plate);

    return plate;
  }

  buildHangerGeometry() {
    const geometries = [];
    const hangerOffset = 4;
    const hangerObj = this.resourceManager.getObject('slimSystem');
    if (!hangerObj) {
      return [];
    }

    hangerObj.children.forEach((z) => {
      const geom = z.geometry.clone();
      Helpers.RotateObject(geom, null, 90, 'X');

      geom.translate(
        this.parameters.hangerMargin,
        hangerOffset,
        this.parameters.height
      );
      const geom2 = geom.clone();
      geom2.translate(
        this.parameters.width - 2 * this.parameters.hangerMargin,
        0,
        0
      );

      if (this.parameters.system === 'hamak') {
        geometries.push(geom);
        geometries.push(geom2);
      }
    });

    hangerObj.traverse((node) => {
      this.disposeObject(node);
    }); // Clean geometries to avoid memory leak

    return geometries;
  }
}
